import {
  DAY_SECONDS,
  formatClock,
  type AdminAnalytics,
  type AdminEventMessage,
  type AdminStateMessage,
  type AgencyStat,
  type CameraStat,
  type IncidentRow,
  type JourneyPoint,
  type OnlineUser,
  type RideAlongStateMessage,
  type CameraDetailReplyMessage,
  type SearchStat,
} from '@hotlist/shared';
import { vehicleStill, vehicleHex } from './captureart.ts';

/**
 * The oversight dashboard.
 *
 * The administrator's own screen, and the one seat in the game that is above
 * the read rule: a full operator's view of the whole county at once. The pulse
 * across the top, a live feed of every read, hit, stop and chase as it lands
 * (with the full plates the officer console is not allowed to keep), and cards
 * for enforcement, search accountability, the busiest poles, a plate lookup,
 * the agency leaderboard, and the county controls.
 *
 * Everything here is server truth. Nothing is invented in the client; the
 * numbers are the ones the simulation keeps and the feed is pushed as it
 * happens. What the dashboard adds is the arrangement -- the thing a real
 * operations picture is, that a row of counters is not.
 */
export interface AdminHandlers {
  refresh: () => void;
  reset: (what: 'cameras' | 'world' | 'players') => void;
  /** Look up a plate's enforcement record: its stops and pursuits. */
  oversight: (plate: string) => void;
  /** Draw a plate's journey -- every camera that read it -- on the map. */
  trace: (points: JourneyPoint[]) => void;
  /** Show the traffic heatmap on the map: where and when the network reads. */
  heatmap: () => void;
  /** Start riding along with a connected player (by vehicle id). */
  rideAlong: (id: number) => void;
  /** Stop riding along. */
  stopRideAlong: () => void;
  /** Open a camera: visit it on the map and see its captures. */
  viewCamera: (id: string) => void;
  /** Drop into a player's seat -- the immersive ride-along. */
  join: (id: number) => void;
}

interface Reset {
  what: 'cameras' | 'world' | 'players';
  title: string;
  does: string;
  keeps: string;
}

const RESETS: readonly Reset[] = Object.freeze([
  {
    what: 'cameras',
    title: 'Cameras',
    does: 'Puts every camera back where the archive said it was, and clears whatever the world had done to them since.',
    keeps: 'The record, the clock, and every player keeps everything.',
  },
  {
    what: 'world',
    title: 'World',
    does: 'A fresh county. Cameras back, the clock back to day one, and the reads, searches, alerts and community map all emptied.',
    keeps: 'Players keep their cars and their money. They did not ask for a new world.',
  },
  {
    what: 'players',
    title: 'Players',
    does: 'Every plate, key and balance gone. Anybody driving is told their keys no longer open anything.',
    keeps: 'The county is untouched: cameras, record and clock all stay as they are.',
  },
]);

/** How long a confirmation stands before it gives up on you. */
const CONFIRM_SECONDS = 8;

/** How many feed rows to keep on screen. */
const FEED_CAP = 60;

export class AdminPanel {
  private readonly handlers: AdminHandlers;
  private armed: Reset['what'] | null = null;
  private timer = 0;
  private daySeconds = DAY_SECONDS;

  private readonly resets = must('admin-body');
  private readonly pulse = must('dash-pulse');
  private readonly clock = must('dash-clock');
  private readonly feed = must('dash-feed');
  private readonly enforcement = must('dash-enforcement');
  private readonly searchCard = must('dash-search');
  private readonly work = must('dash-work');
  private readonly cameras = must('dash-cameras');
  private readonly agencies = must('dash-agencies');
  private readonly hotlistCard = must('dash-hotlist');
  private readonly downCard = must('dash-down');
  private readonly platesCard = must('dash-plates');
  private readonly categoriesCard = must('dash-categories');
  private readonly onlineCard = must('dash-online');
  private readonly rideCard = must('dash-ridealong');
  private readonly rideWrap = must('dash-ridealong-card');
  private readonly camDetail = must('dash-camdetail');
  private readonly camWrap = must('dash-camdetail-card');
  /** The feed keeps a buffer so filtering and pausing can re-derive what shows. */
  private readonly feedBuffer: AdminEventMessage[] = [];
  private feedKind = 'all';
  private feedQuery = '';
  private feedPaused = false;
  private lastRideId: number | null = null;
  private readonly oversightStatus = must('admin-oversight-status');
  private readonly oversightResults = must('admin-oversight-results');
  private readonly oversightModal = must('oversight-modal') as HTMLDialogElement;
  private readonly oversightTrace = must('oversight-trace') as HTMLButtonElement;
  /** The last looked-up plate's journey, held so "trace on map" can draw it. */
  private journey: JourneyPoint[] = [];

  constructor(handlers: AdminHandlers) {
    this.handlers = handlers;

    const oversightForm = must('admin-oversight-form') as HTMLFormElement;
    const oversightPlate = must('admin-oversight-plate') as HTMLInputElement;
    oversightForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const plate = oversightPlate.value.trim().toUpperCase();
      if (!plate) return;
      this.oversightStatus.textContent = 'looking up ' + plate;
      this.oversightResults.replaceChildren();
      // The record opens in its own window so the incident detail has room to
      // read whole instead of being clipped inside the lookup card.
      if (!this.oversightModal.open) this.oversightModal.showModal();
      this.handlers.oversight(plate);
    });
    must('oversight-close').addEventListener('click', () => this.oversightModal.close());
    // Draw the journey and let the map show through -- trace() steps the dashboard
    // aside so the path is on the county, at real scale.
    this.oversightTrace.addEventListener('click', () => {
      if (this.journey.length === 0) return;
      this.oversightModal.close();
      this.handlers.trace(this.journey);
    });
    // Click the dimmed backdrop (outside the dialog box) to dismiss.
    this.oversightModal.addEventListener('click', (event) => {
      if (event.target === this.oversightModal) this.oversightModal.close();
    });

    this.resets.addEventListener('click', (event) => {
      const target = event.target as HTMLElement;
      const what = target.dataset.reset as Reset['what'] | undefined;
      if (what) {
        this.arm(what);
        return;
      }
      const confirmed = target.dataset.confirm as Reset['what'] | undefined;
      if (confirmed) {
        this.disarm();
        this.handlers.reset(confirmed);
      }
      if (target.dataset.cancel !== undefined) this.disarm();
    });

    (must('admin-refresh') as HTMLButtonElement).addEventListener('click', () =>
      this.handlers.refresh(),
    );
    (must('admin-heatmap') as HTMLButtonElement).addEventListener('click', () =>
      this.handlers.heatmap(),
    );

    // The feed's controls: filter by kind, search by plate, freeze it to read.
    for (const button of document.querySelectorAll<HTMLElement>('.feed-filter')) {
      button.addEventListener('click', () => {
        this.feedKind = button.dataset.kind ?? 'all';
        for (const b of document.querySelectorAll<HTMLElement>('.feed-filter')) {
          b.classList.toggle('is-on', b === button);
        }
        this.renderFeed();
      });
    }
    const search = must('feed-search') as HTMLInputElement;
    search.addEventListener('input', () => {
      this.feedQuery = search.value.trim().toUpperCase();
      this.renderFeed();
    });
    const pause = must('feed-pause') as HTMLButtonElement;
    pause.addEventListener('click', () => {
      this.feedPaused = !this.feedPaused;
      pause.textContent = this.feedPaused ? 'resume' : 'pause';
      pause.classList.toggle('is-on', this.feedPaused);
      if (!this.feedPaused) this.renderFeed();
    });

    // Click a connected player -- or any car in the feed -- to visit it.
    const rideOnClick = (event: Event): void => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-ride]');
      const id = row?.dataset.ride;
      if (id) this.handlers.rideAlong(Number(id));
    };
    this.onlineCard.addEventListener('click', rideOnClick);
    this.feed.addEventListener('click', rideOnClick);
    (must('ride-stop') as HTMLButtonElement).addEventListener('click', () => {
      this.handlers.stopRideAlong();
      this.rideWrap.hidden = true;
    });
    (must('ride-join') as HTMLButtonElement).addEventListener('click', () => {
      if (this.lastRideId !== null) this.handlers.join(this.lastRideId);
    });

    // Click a camera in the busiest list to visit it and see its captures.
    this.cameras.addEventListener('click', (event) => {
      const row = (event.target as HTMLElement).closest<HTMLElement>('[data-camera]');
      const id = row?.dataset.camera;
      if (id) this.handlers.viewCamera(id);
    });
    (must('cam-close') as HTMLButtonElement).addEventListener('click', () => {
      this.camWrap.hidden = true;
    });
    (must('cam-min') as HTMLButtonElement).addEventListener('click', () => {
      this.camWrap.classList.toggle('minimized');
    });

    this.paintResets();
  }

  /** A full refresh: the pulse, the analytics cards, and the feed backlog. */
  show(state: AdminStateMessage): void {
    this.clock.textContent = `day ${state.day}`;
    const a = state.analytics;
    if (a) {
      this.paintPulse(state, a);
      this.paintEnforcement(a);
      this.paintWork(a);
      this.paintSearch(a);
      this.paintHotlist(a);
      this.paintDownCameras(a);
      this.paintTopPlates(a);
      this.paintCategories(a);
      this.paintOnline(a);
      this.paintCameras(a);
      this.paintAgencies(a);
    }
    // Seed the feed buffer once; live events push onto it after that.
    if (state.feed && this.feedBuffer.length === 0) {
      this.feedBuffer.push(...state.feed);
      this.renderFeed();
    }
    if (!state.durable) {
      const warn = document.createElement('div');
      warn.className = 'admin-warn';
      warn.textContent =
        'The camera store did not open, so the deployment already resets on every restart and ' +
        'nothing the world does to it is being kept.';
      this.pulse.append(warn);
    }
  }

  /** One live event: buffered always, shown if it passes the filter and is not paused. */
  event(ev: AdminEventMessage): void {
    this.feedBuffer.unshift(ev);
    if (this.feedBuffer.length > 300) this.feedBuffer.length = 300;
    if (this.feedPaused || !this.passes(ev)) return;
    this.feed.prepend(this.feedRow(ev));
    while (this.feed.childElementCount > FEED_CAP) {
      this.feed.lastElementChild?.remove();
    }
  }

  private passes(ev: AdminEventMessage): boolean {
    if (this.feedKind !== 'all' && ev.kind !== this.feedKind) return false;
    if (this.feedQuery !== '' && !ev.plate.toUpperCase().includes(this.feedQuery)) return false;
    return true;
  }

  private renderFeed(): void {
    const rows = this.feedBuffer.filter((ev) => this.passes(ev)).slice(0, FEED_CAP);
    this.feed.replaceChildren(...rows.map((ev) => this.feedRow(ev)));
  }

  private feedRow(ev: AdminEventMessage): HTMLElement {
    const row = document.createElement('div');
    row.className = `feed-row feed-${ev.kind}${ev.flag ? ' feed-flag-' + ev.flag : ''}`;
    row.dataset.ride = String(ev.id);

    const when = document.createElement('span');
    when.className = 'feed-when';
    when.textContent = formatClock(ev.at, this.daySeconds);

    const kind = document.createElement('span');
    kind.className = 'feed-kind';
    kind.textContent = ev.flag || ev.kind;

    const plate = document.createElement('span');
    plate.className = 'feed-plate';
    plate.textContent = ev.plate;

    const detail = document.createElement('span');
    detail.className = 'feed-detail';
    detail.textContent = ev.place ? `${ev.place} · ${ev.detail}` : ev.detail;

    row.append(when, kind, plate, detail);
    return row;
  }

  private paintPulse(state: AdminStateMessage, a: AdminAnalytics): void {
    const wrongfulPct = a.stops > 0 ? Math.round((a.wrongfulStops / a.stops) * 100) : 0;
    const unexplainedPct =
      a.searchesTotal > 0 ? Math.round((a.searchesUnexplained / a.searchesTotal) * 100) : 0;
    const tiles: [string, string, string, string][] = [
      ['cars on the road', a.carsOnRoad.toLocaleString(), '', ''],
      ['cameras live', a.camerasLive.toLocaleString(), `${a.camerasDown} down`, a.camerasDown > 0 ? 'warn' : ''],
      ['reads on record', a.readsTotal.toLocaleString(), `${a.readsLastHour.toLocaleString()} this hour`, ''],
      ['hotlist', a.hotlistActive.toLocaleString(), `${a.hotlistStale} stale`, a.hotlistStale > 0 ? 'warn' : ''],
      ['stops', a.stops.toLocaleString(), `${wrongfulPct}% wrongful`, wrongfulPct >= 40 ? 'bad' : ''],
      ['searches', a.searchesTotal.toLocaleString(), `${unexplainedPct}% no reason`, unexplainedPct >= 50 ? 'warn' : ''],
      ['pursuits', (a.pursuits.pursued).toLocaleString(), `${a.pursuits.cited} caught · ${a.pursuits.eluded} away`, ''],
      ['revenue', `$${a.revenue.toLocaleString()}`, `${a.citations} citations`, ''],
      ['registrations', state.accounts.toLocaleString(), `${state.players} online`, ''],
    ];
    this.pulse.replaceChildren();
    for (const [label, value, sub, tone] of tiles) {
      const tile = document.createElement('div');
      tile.className = 'pulse-tile' + (tone ? ' pulse-' + tone : '');
      const v = document.createElement('div');
      v.className = 'pulse-value';
      v.textContent = value;
      const l = document.createElement('div');
      l.className = 'pulse-label';
      l.textContent = label;
      tile.append(v, l);
      if (sub) {
        const s = document.createElement('div');
        s.className = 'pulse-sub';
        s.textContent = sub;
        tile.append(s);
      }
      if (label === 'reads on record') {
        const spark = document.createElement('canvas');
        spark.className = 'pulse-spark';
        spark.width = 260;
        spark.height = 32;
        tile.append(spark);
        drawSparkline(spark, a.readsSeries);
      }
      this.pulse.append(tile);
    }
  }

  private paintEnforcement(a: AdminAnalytics): void {
    this.enforcement.replaceChildren();
    const wrongfulPct = a.stops > 0 ? Math.round((a.wrongfulStops / a.stops) * 100) : 0;
    const falsePct = a.alerts > 0 ? Math.round((a.falseAlerts / a.alerts) * 100) : 0;
    // Stops, split into good and wrongful.
    this.enforcement.append(
      statLine('stops made', String(a.stops)),
      splitBar([
        { label: 'good', value: a.stops - a.wrongfulStops, cls: 'good' },
        { label: 'wrongful', value: a.wrongfulStops, cls: 'bad' },
      ]),
      note(`${wrongfulPct}% of stops were on nothing, a stale flag, or the wrong car.`),
      statLine('alerts fired', String(a.alerts)),
      splitBar([
        { label: 'on a real match', value: a.alerts - a.falseAlerts, cls: 'good' },
        { label: 'misread / false', value: a.falseAlerts, cls: 'warn' },
      ]),
      note(`${falsePct}% of alerts were a camera misreading a plate onto the list.`),
      statLine('pursuits', String(a.pursuits.pursued)),
      splitBar([
        { label: 'caught', value: a.pursuits.cited, cls: 'accent' },
        { label: 'eluded', value: a.pursuits.eluded, cls: 'dim' },
      ]),
      statLine('citations', String(a.citations)),
      statLine('revenue', `$${a.revenue.toLocaleString()}`),
      splitBar([
        { label: 'officers', value: a.revenuePlayer, cls: 'accent' },
        { label: 'patrol', value: a.revenueNpc, cls: 'good' },
      ]),
      statLine('officers', `$${a.revenuePlayer.toLocaleString()}`),
      statLine('patrol', `$${a.revenueNpc.toLocaleString()}`),
    );
  }

  private paintWork(a: AdminAnalytics): void {
    this.work.replaceChildren();
    const w = a.work;
    const earned = w.earnedLegal + w.earnedIllegal;
    const illegalPct = earned > 0 ? Math.round((w.earnedIllegal / earned) * 100) : 0;
    this.work.append(
      statLine('jobs finished', w.jobsDone.toLocaleString()),
      statLine('paid out', `$${earned.toLocaleString()}`),
      splitBar([
        { label: 'legal', value: w.earnedLegal, cls: 'good' },
        { label: 'illegal', value: w.earnedIllegal, cls: 'bad' },
      ]),
      note(`${illegalPct}% of what players earned came from illegal work -- the jobs that get a plate reported and put on the list.`),
      statLine('on the board now', String(w.offered)),
      splitBar([
        { label: 'legal', value: w.offered - w.offeredIllegal, cls: 'good' },
        { label: 'illegal', value: w.offeredIllegal, cls: 'bad' },
      ]),
      statLine('being run now', String(w.inProgress)),
      splitBar([
        { label: 'legal', value: w.inProgress - w.inProgressIllegal, cls: 'good' },
        { label: 'illegal', value: w.inProgressIllegal, cls: 'bad' },
      ]),
    );
  }

  private paintSearch(a: AdminAnalytics): void {
    this.searchCard.replaceChildren();
    const unexplainedPct =
      a.searchesTotal > 0 ? Math.round((a.searchesUnexplained / a.searchesTotal) * 100) : 0;
    this.searchCard.append(
      statLine('searches run', String(a.searchesTotal)),
      splitBar([
        { label: 'with a reason', value: a.searchesTotal - a.searchesUnexplained, cls: 'good' },
        { label: 'no reason given', value: a.searchesUnexplained, cls: 'warn' },
      ]),
      note(`${unexplainedPct}% were run with no reason typed. Nothing in the game required one.`),
    );
    const recent = document.createElement('div');
    recent.className = 'dash-list';
    if (a.recentSearches.length === 0) {
      recent.append(note('No searches yet.'));
    } else {
      for (const s of a.recentSearches) recent.append(this.searchRow(s));
    }
    this.searchCard.append(recent);
  }

  private searchRow(s: SearchStat): HTMLElement {
    const row = document.createElement('div');
    row.className = 'dash-row' + (s.reason.trim() === '' ? ' dash-row-flag' : '');
    const unit = document.createElement('span');
    unit.className = 'dash-row-key';
    unit.textContent = s.unit || '(unknown)';
    const term = document.createElement('span');
    term.className = 'dash-row-mid';
    term.textContent = s.term;
    const reason = document.createElement('span');
    reason.className = 'dash-row-end';
    reason.textContent = s.reason.trim() === '' ? 'no reason' : s.reason;
    row.append(unit, term, reason);
    return row;
  }

  private paintHotlist(a: AdminAnalytics): void {
    this.hotlistCard.replaceChildren();
    if (a.hotlist.length === 0) {
      this.hotlistCard.append(note('Nothing on the list.'));
      return;
    }
    for (const entry of a.hotlist) {
      const row = document.createElement('div');
      row.className = 'hot-row' + (entry.resolved ? ' hot-stale' : '');
      const plate = document.createElement('span');
      plate.className = 'hot-plate';
      plate.textContent = entry.plate;
      const chip = document.createElement('span');
      chip.className = `chip chip-${entry.reason}`;
      chip.textContent = entry.reason;
      const meta = document.createElement('span');
      meta.className = 'hot-meta';
      meta.textContent = `${entry.resolved ? 'stale · ' : ''}${Math.round(entry.ageHours)}h`;
      row.append(plate, chip, meta);
      this.hotlistCard.append(row);
    }
  }

  private paintDownCameras(a: AdminAnalytics): void {
    this.downCard.replaceChildren();
    if (a.downCameras.length === 0) {
      const ok = note('Every camera is reading.');
      ok.classList.add('all-clear');
      this.downCard.append(ok);
      return;
    }
    for (const camera of a.downCameras) {
      const row = document.createElement('div');
      row.className = 'down-row';
      const road = document.createElement('span');
      road.className = 'down-road';
      road.textContent = camera.road + (camera.agency ? ' · ' + shorten(camera.agency) : '');
      const chip = document.createElement('span');
      chip.className = 'chip chip-down';
      chip.textContent = camera.covered;
      row.append(road, chip);
      this.downCard.append(row);
    }
  }

  private paintTopPlates(a: AdminAnalytics): void {
    this.platesCard.replaceChildren();
    if (a.topPlates.length === 0) {
      this.platesCard.append(note('No plate has drawn a reaction yet.'));
      return;
    }
    const head = document.createElement('div');
    head.className = 'plate-row plate-head';
    for (const [text, cls] of [
      ['plate', 'plate-tag'],
      ['alerts', 'plate-num'],
      ['stops', 'plate-num'],
      ['wrongful', 'plate-num'],
    ] as const) {
      const cell = document.createElement('span');
      cell.className = cls;
      cell.textContent = text;
      head.append(cell);
    }
    this.platesCard.append(head);
    for (const plate of a.topPlates) {
      const row = document.createElement('div');
      row.className = 'plate-row';
      const tag = document.createElement('span');
      tag.className = 'plate-tag';
      tag.textContent = plate.plate;
      const alerts = document.createElement('span');
      alerts.className = 'plate-num';
      alerts.textContent = String(plate.alerts);
      const stops = document.createElement('span');
      stops.className = 'plate-num';
      stops.textContent = String(plate.stops);
      const wrongful = document.createElement('span');
      wrongful.className = 'plate-num' + (plate.wrongful > 0 ? ' bad' : '');
      wrongful.textContent = String(plate.wrongful);
      row.append(tag, alerts, stops, wrongful);
      this.platesCard.append(row);
    }
  }

  private paintCategories(a: AdminAnalytics): void {
    this.categoriesCard.replaceChildren();
    const c = a.categories;
    const total = c.police + c.sheriff + c.vendor + c.private + c.unattributed;
    const rows: [string, number, string][] = [
      ['police', c.police, 'cat-police'],
      ['sheriff', c.sheriff, 'cat-sheriff'],
      ['vendor · Flock', c.vendor, 'cat-vendor'],
      ['private', c.private, 'cat-private'],
      ['unattributed', c.unattributed, 'cat-unattributed'],
    ];
    const bar = document.createElement('div');
    bar.className = 'split-bar';
    for (const [, value, cls] of rows) {
      if (value <= 0) continue;
      const seg = document.createElement('div');
      seg.className = 'split-seg ' + cls;
      seg.style.width = `${(value / Math.max(1, total)) * 100}%`;
      bar.append(seg);
    }
    this.categoriesCard.append(bar);
    for (const [label, value, cls] of rows) {
      const row = document.createElement('div');
      row.className = 'cat-legend';
      const dot = document.createElement('span');
      dot.className = 'cat-dot ' + cls;
      const name = document.createElement('span');
      name.className = 'cat-name';
      name.textContent = label;
      const num = document.createElement('span');
      num.className = 'cat-num';
      num.textContent = String(value);
      row.append(dot, name, num);
      this.categoriesCard.append(row);
    }
    this.categoriesCard.append(
      note('Not all readers are police. Vendor and private cameras feed the same searchable pool.'),
    );
  }

  private paintOnline(a: AdminAnalytics): void {
    this.onlineCard.replaceChildren();
    if (a.onlineUsers.length === 0) {
      this.onlineCard.append(note('Nobody connected.'));
      return;
    }
    for (const user of a.onlineUsers) {
      const row = document.createElement('div');
      row.className = 'online-row';
      row.dataset.ride = String(user.id);
      const plate = document.createElement('span');
      plate.className = 'online-plate';
      plate.textContent = user.plate;
      const side = document.createElement('span');
      side.className = 'chip side-' + user.side;
      side.textContent = user.side;
      const act = document.createElement('span');
      act.className = 'online-act' + (user.wanted ? ' wanted' : '');
      act.textContent = user.activity;
      row.append(plate, side, act);
      this.onlineCard.append(row);
    }
    this.onlineCard.append(note('Click a player to ride along.'));
  }

  /** The player the operator is riding along with: their whole live state. */
  showRideAlong(state: RideAlongStateMessage): void {
    this.lastRideId = state.id;
    this.rideWrap.hidden = false;
    this.rideCard.replaceChildren();
    const earned = state.earnedLegal + state.earnedIllegal;
    const illegalPct = earned > 0 ? Math.round((state.earnedIllegal / earned) * 100) : 0;
    const status = state.pursued ? 'BEING CHASED' : state.wanted ? 'WANTED' : 'clear';

    const head = document.createElement('div');
    head.className = 'ride-head' + (state.pursued ? ' ride-chased' : state.wanted ? ' ride-wanted' : '');
    const tag = document.createElement('span');
    tag.className = 'ride-plate';
    tag.textContent = state.plate;
    const chip = document.createElement('span');
    chip.className = 'chip side-' + state.side;
    chip.textContent = state.side;
    const st = document.createElement('span');
    st.className = 'ride-status';
    st.textContent = status;
    head.append(tag, chip, st);
    this.rideCard.append(head);

    const lines: [string, string][] = [
      ['on', `${state.road} · ${state.mph} mph`],
      ['money', `$${state.money.toLocaleString()}`],
      ['earned', `$${earned.toLocaleString()} · ${illegalPct}% illegal`],
      ['jobs done', String(state.jobsDone)],
    ];
    if (state.agency) lines.splice(1, 0, ['agency', shorten(state.agency)]);
    for (const [k, v] of lines) this.rideCard.append(statLine(k, v));

    if (state.job) {
      const job = document.createElement('div');
      job.className = 'ride-job' + (state.job.illegal ? ' illegal' : '');
      job.textContent =
        `${state.job.illegal ? 'ILLEGAL JOB' : 'legal job'} — ${state.job.from} → ${state.job.to} ` +
        `· $${state.job.pays} · ${state.job.stage}`;
      this.rideCard.append(job);
    }
    if (state.recent.length > 0) {
      const trail = document.createElement('div');
      trail.className = 'ride-trail';
      for (const line of state.recent.slice(0, 6)) {
        const row = document.createElement('div');
        row.textContent = line;
        trail.append(row);
      }
      this.rideCard.append(trail);
    }
  }

  /** A camera opened: its detail and its captures -- what it is actually reading. */
  showCameraDetail(state: CameraDetailReplyMessage): void {
    this.camWrap.hidden = false;
    this.camWrap.classList.remove('minimized');
    this.camDetail.replaceChildren();

    const head = document.createElement('div');
    head.className = 'cam-detail-head';
    const road = document.createElement('div');
    road.className = 'cam-detail-road';
    road.textContent = state.road;
    const sub = document.createElement('div');
    sub.className = 'cam-detail-sub';
    const dot = document.createElement('span');
    dot.className = 'cat-dot cat-' + state.category;
    sub.append(
      dot,
      document.createTextNode(
        `${state.agency || 'unattributed'} · ${state.category} · ${state.place}`,
      ),
    );
    head.append(road, sub);
    this.camDetail.append(head);

    this.camDetail.append(
      statLine('reads', state.reads.toLocaleString()),
      statLine('facing', `${compass8(state.bearing)} (${state.bearing}°)`),
      statLine('setback', `${state.setback} m`),
      statLine('status', state.down ? `DOWN — ${state.coveredWith || 'obstructed'}` : 'reading'),
    );

    const capsHead = document.createElement('div');
    capsHead.className = 'cam-caps-head';
    capsHead.textContent = `captures (${state.captures.length})`;
    this.camDetail.append(capsHead);
    if (state.captures.length === 0) {
      this.camDetail.append(note('No captures on record.'));
    } else {
      const list = document.createElement('div');
      list.className = 'cam-caps';
      for (const capture of state.captures) {
        const cap = document.createElement('div');
        cap.className = 'cam-cap';

        // The still: the vehicle the reader classified, painted its read color,
        // framed and annotated the way an ALPR capture is.
        const frame = document.createElement('div');
        frame.className = 'cap-frame';
        frame.append(vehicleStill(capture.color, capture.make, capture.body, capture.plate, capture.jurisdiction));
        const clock = document.createElement('div');
        clock.className = 'cap-timestamp';
        clock.textContent = formatClock(capture.at, this.daySeconds);
        frame.append(clock);
        cap.append(frame);

        const body = document.createElement('div');
        body.className = 'cap-body';
        const print = document.createElement('div');
        print.className = 'cap-print';
        const swatch = document.createElement('span');
        swatch.className = 'cap-swatch';
        swatch.style.background = vehicleHex(capture.color);
        print.append(
          swatch,
          document.createTextNode(
            `${capture.color} ${capture.make} ${capture.body}`.replace(/\b\w/g, (c) => c.toUpperCase()),
          ),
        );
        const meta = document.createElement('div');
        meta.className = 'cap-meta';
        meta.textContent = `${formatClock(capture.at, this.daySeconds)} · ${compass8(capture.bearing)} · ${capture.mph} mph`;
        body.append(print, meta);
        cap.append(body);
        list.append(cap);
      }
      this.camDetail.append(list);
    }
  }

  private paintCameras(a: AdminAnalytics): void {
    this.cameras.replaceChildren();
    const max = Math.max(1, ...a.topCameras.map((c) => c.reads));
    if (a.topCameras.length === 0) {
      this.cameras.append(note('No reads yet.'));
      return;
    }
    for (const camera of a.topCameras) this.cameras.append(this.cameraRow(camera, max));
  }

  private cameraRow(camera: CameraStat, max: number): HTMLElement {
    const row = document.createElement('div');
    row.className = 'cam-row' + (camera.down ? ' cam-down' : '');
    row.dataset.camera = camera.id;
    const label = document.createElement('div');
    label.className = 'cam-label';
    label.textContent = `${camera.road}${camera.agency ? ' · ' + shorten(camera.agency) : ''}`;
    const track = document.createElement('div');
    track.className = 'cam-track';
    const fill = document.createElement('div');
    fill.className = 'cam-fill cat-' + camera.category;
    fill.style.width = `${Math.round((camera.reads / max) * 100)}%`;
    track.append(fill);
    const count = document.createElement('span');
    count.className = 'cam-count';
    count.textContent = camera.down ? 'down' : camera.reads.toLocaleString();
    row.append(label, track, count);
    return row;
  }

  private paintAgencies(a: AdminAnalytics): void {
    this.agencies.replaceChildren();
    const head = document.createElement('div');
    head.className = 'agency-row agency-head';
    for (const [text, cls] of [
      ['agency', 'agency-name'],
      ['cams', 'agency-num'],
      ['searches', 'agency-num'],
      ['stops', 'agency-num'],
      ['wrongful', 'agency-num'],
      ['closed', 'agency-num'],
      ['revenue', 'agency-num'],
    ] as const) {
      const cell = document.createElement('span');
      cell.className = cls;
      cell.textContent = text;
      head.append(cell);
    }
    this.agencies.append(head);

    if (a.agencies.length === 0) {
      this.agencies.append(note('No agency activity yet.'));
      return;
    }
    for (const agency of a.agencies) this.agencies.append(this.agencyRow(agency));
  }

  private agencyRow(agency: AgencyStat): HTMLElement {
    const wrongfulPct = agency.stops > 0 ? Math.round((agency.wrongful / agency.stops) * 100) : 0;
    const row = document.createElement('div');
    row.className = 'agency-row';
    const cells: [string, string, string][] = [
      [shorten(agency.name), 'agency-name', ''],
      [`${agency.cameras}${agency.down ? ` (${agency.down}↓)` : ''}`, 'agency-num', ''],
      [String(agency.searches), 'agency-num', ''],
      [String(agency.stops), 'agency-num', ''],
      [agency.stops > 0 ? `${wrongfulPct}%` : '—', 'agency-num', wrongfulPct >= 40 ? 'bad' : ''],
      [String(agency.casesClosed), 'agency-num', ''],
      [`$${agency.revenue.toLocaleString()}`, 'agency-num', ''],
    ];
    for (const [text, cls, tone] of cells) {
      const cell = document.createElement('span');
      cell.className = cls + (tone ? ' ' + tone : '');
      if (cls === 'agency-name') {
        const dot = document.createElement('span');
        dot.className = 'cat-dot cat-' + agency.category;
        cell.append(dot, document.createTextNode(text));
      } else {
        cell.textContent = text;
      }
      row.append(cell);
    }
    return row;
  }

  private arm(what: Reset['what']): void {
    this.armed = what;
    this.paintResets();
    window.clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.disarm(), CONFIRM_SECONDS * 1000);
  }

  private disarm(): void {
    this.armed = null;
    window.clearTimeout(this.timer);
    this.paintResets();
  }

  private paintResets(): void {
    this.resets.replaceChildren();
    for (const reset of RESETS) {
      const card = document.createElement('div');
      card.className = 'admin-card';

      const title = document.createElement('div');
      title.className = 'admin-card-title';
      title.textContent = `Reset ${reset.title}`;
      card.append(title);

      const does = document.createElement('p');
      does.className = 'admin-does';
      does.textContent = reset.does;
      card.append(does);

      const keeps = document.createElement('p');
      keeps.className = 'admin-keeps';
      keeps.textContent = reset.keeps;
      card.append(keeps);

      if (this.armed === reset.what) {
        const row = document.createElement('div');
        row.className = 'admin-confirm';
        const go = document.createElement('button');
        go.type = 'button';
        go.className = 'admin-go';
        go.dataset.confirm = reset.what;
        go.textContent = `Yes, reset ${reset.title.toLowerCase()}`;
        const no = document.createElement('button');
        no.type = 'button';
        no.className = 'admin-no';
        no.dataset.cancel = '';
        no.textContent = 'Leave it';
        row.append(go, no);
        card.append(row);
      } else {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'admin-arm';
        button.dataset.reset = reset.what;
        button.textContent = `Reset ${reset.title.toLowerCase()}`;
        card.append(button);
      }
      this.resets.append(card);
    }
  }

  /** What a finished reset actually did, kept on screen rather than toasted away. */
  said(message: string): void {
    const note_ = document.createElement('div');
    note_.className = 'admin-said';
    note_.textContent = message;
    this.pulse.prepend(note_);
  }

  /** The county's day, so incident timestamps agree with the world. */
  setDaySeconds(seconds: number): void {
    if (Number.isFinite(seconds) && seconds > 0) this.daySeconds = seconds;
  }

  /** A plate's record: the journey (camera reads) and the enforcement incidents. */
  showOversight(plate: string, incidents: IncidentRow[], journey: JourneyPoint[]): void {
    this.journey = journey;
    const reads = journey.length;
    this.oversightStatus.textContent =
      reads || incidents.length
        ? `${plate}: ${reads} read${reads === 1 ? '' : 's'}, ${incidents.length} incident${incidents.length === 1 ? '' : 's'}`
        : `${plate}: nothing on record`;
    this.oversightTrace.hidden = reads === 0;
    this.oversightTrace.textContent = `trace ${reads} on map`;
    this.oversightResults.replaceChildren();
    for (const incident of incidents) {
      const line = document.createElement('div');
      line.className = `query-incident inc-${incident.outcome}`;
      const when = document.createElement('span');
      when.className = 'query-when';
      when.textContent = formatClock(incident.at, this.daySeconds);
      const tag = document.createElement('span');
      tag.className = 'query-incident-tag';
      tag.textContent = incident.outcome;
      const detail = document.createElement('span');
      detail.className = 'query-incident-detail';
      detail.textContent = incident.detail;
      line.append(when, tag, detail);
      this.oversightResults.append(line);
    }
  }
}

function drawSparkline(canvas: HTMLCanvasElement, series: number[]): void {
  const ctx = canvas.getContext('2d');
  if (!ctx || series.length < 2) return;
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  let max = 1;
  for (const v of series) if (v > max) max = v;
  const pad = 2;
  const xAt = (i: number): number => pad + (i / (series.length - 1)) * (w - 2 * pad);
  const yAt = (v: number): number => h - pad - (v / max) * (h - 2 * pad);

  // A faint baseline, so the line has a floor to read against.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.06)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, h - pad - 0.5);
  ctx.lineTo(w, h - pad - 0.5);
  ctx.stroke();

  // The area under the line, an amber wash.
  ctx.beginPath();
  ctx.moveTo(xAt(0), yAt(series[0] ?? 0));
  for (let i = 1; i < series.length; i++) ctx.lineTo(xAt(i), yAt(series[i] ?? 0));
  ctx.lineTo(xAt(series.length - 1), h - pad);
  ctx.lineTo(xAt(0), h - pad);
  ctx.closePath();
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, 'rgba(255, 176, 32, 0.35)');
  grad.addColorStop(1, 'rgba(255, 176, 32, 0.02)');
  ctx.fillStyle = grad;
  ctx.fill();

  // The line itself.
  ctx.beginPath();
  ctx.moveTo(xAt(0), yAt(series[0] ?? 0));
  for (let i = 1; i < series.length; i++) ctx.lineTo(xAt(i), yAt(series[i] ?? 0));
  ctx.strokeStyle = 'rgba(255, 176, 32, 0.9)';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // The now, emphasized: a dot at the newest bucket.
  const last = series.length - 1;
  ctx.beginPath();
  ctx.arc(xAt(last), yAt(series[last] ?? 0), 2.2, 0, Math.PI * 2);
  ctx.fillStyle = '#ffb020';
  ctx.fill();
}

/** A compass bearing to an eight-point name. */
function compass8(deg: number): string {
  if (deg < 0) return '—';
  const names = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return names[Math.round(deg / 45) % 8] as string;
}

function statLine(label: string, value: string): HTMLElement {
  const row = document.createElement('div');
  row.className = 'stat-line';
  const l = document.createElement('span');
  l.textContent = label;
  const v = document.createElement('span');
  v.className = 'stat-value';
  v.textContent = value;
  row.append(l, v);
  return row;
}

function splitBar(parts: { label: string; value: number; cls: string }[]): HTMLElement {
  const total = Math.max(1, parts.reduce((s, p) => s + p.value, 0));
  const bar = document.createElement('div');
  bar.className = 'split-bar';
  for (const part of parts) {
    if (part.value <= 0) continue;
    const seg = document.createElement('div');
    seg.className = `split-seg split-${part.cls}`;
    seg.style.width = `${(part.value / total) * 100}%`;
    seg.title = `${part.label}: ${part.value}`;
    bar.append(seg);
  }
  return bar;
}

function note(text: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'dash-note';
  el.textContent = text;
  return el;
}

/** Trim the long official agency names to something a column can hold. */
function shorten(name: string): string {
  return name
    .replace(/ Police Department$/, ' PD')
    .replace(/ Sheriff'?s Department$/, ' Sheriff')
    .replace(/ Sheriff'?s Office$/, ' Sheriff')
    .replace(/^(City|Town|Village) of /, '');
}


function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
